import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Clock, Flame, PackageCheck, CircleAlert } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "pending", label: "Order Received", icon: Check },
  { key: "preparing", label: "Preparing", icon: Flame },
  { key: "ready", label: "Ready for Pickup", icon: PackageCheck },
];

export default function OrderStatus() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [paymentPending, setPaymentPending] = useState(!!sessionId);
  const [paymentTimedOut, setPaymentTimedOut] = useState(false);
  const pollAttempts = useRef(0);

  // Poll Stripe payment status after redirect back from Stripe Checkout
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer;

    const checkPayment = async () => {
      if (cancelled) return;
      if (pollAttempts.current >= 15) {
        // Stop the spinner but keep an actionable banner up so fans don't
        // sit on "Confirming…" forever if the Stripe webhook is slow. The
        // separate order poll below will clear it silently the moment the
        // order flips to paid.
        setPaymentTimedOut(true);
        return;
      }
      pollAttempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentPending(false);
          setPaymentTimedOut(false);
          // Strip session_id from URL now that we're confirmed
          setSearchParams({}, { replace: true });
        } else if (data.status === "expired") {
          setError("Payment session expired. Please try again.");
        } else {
          timer = setTimeout(checkPayment, 2000);
        }
      } catch {
        timer = setTimeout(checkPayment, 2000);
      }
    };
    checkPayment();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    let mounted = true;
    const fetchOrder = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (!mounted) return;
        setOrder(data);
        // Belt-and-suspenders: if the payment-status poll gave up but the
        // webhook has since flipped the order to paid, drop the spinner as
        // soon as this order poll notices.
        if (data.payment_status === "paid") {
          setPaymentPending(false);
          setPaymentTimedOut(false);
        }
      } catch (e) {
        if (mounted) setError("Order not found");
      }
    };
    fetchOrder();
    const interval = setInterval(fetchOrder, 3000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [orderId]);

  if (error) {
    return (
      <div className="min-h-screen bg-white text-slate-900 px-6 py-20 text-center">
        <div className="font-heading text-4xl tracking-wider">{error}</div>
        <button
          onClick={() => navigate("/menu")}
          className="mt-8 bg-[#1d4ed8] px-6 py-3 font-heading text-lg tracking-wider text-white border border-slate-900"
        >
          BACK TO MENU
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="font-heading text-2xl tracking-wider text-slate-400">
          LOADING...
        </div>
      </div>
    );
  }

  const stepIdx = STEPS.findIndex((s) => s.key === order.status);
  const isReady = order.status === "ready";
  const isCompleted = order.status === "completed";
  // Elapsed since status was last changed. When status===ready, this is how
  // long the order has been sitting on the counter. >15min triggers a
  // friendly-but-firm overdue nudge.
  const readyElapsedMin = isReady
    ? Math.floor((Date.now() - new Date(order.updated_at || order.created_at).getTime()) / 60000)
    : 0;
  const isOverdue = isReady && readyElapsedMin >= 15;

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-12">
      <header className="border-b-2 border-slate-900 bg-white px-5 py-4 sm:px-8">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <div className="font-heading text-xl tracking-wider">ORDER STATUS</div>
          <button
            data-testid="new-order-btn"
            onClick={() => navigate("/menu")}
            className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-[#1d4ed8]"
          >
            New Order
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-8">
        {!isCompleted && !paymentPending && order.payment_status !== "unpaid" && (
          <div
            data-testid="pickup-reminder-banner"
            className="mb-4 border-2 border-[#1d4ed8] bg-blue-50 p-3 text-center text-sm text-slate-900"
          >
            <span className="font-heading tracking-wider text-[#1d4ed8]">
              🍔 QUICK REMINDER
            </span>
            <div className="mt-1 text-slate-700">
              Your delicious food will be ready soon! Please pick up your order
              when prompted, within 5–10 minutes.
            </div>
          </div>
        )}

        {isOverdue && (
          <div
            data-testid="ready-overdue-alert"
            className="mb-4 border-2 border-red-600 bg-red-50 p-4"
          >
            <div className="font-heading text-lg tracking-wider text-red-700">
              PLEASE PICK UP NOW
            </div>
            <div className="mt-1 text-sm text-slate-700">
              Your order has been ready for <strong>{readyElapsedMin} minutes</strong>.
              Head to the concession window right away — hot food doesn't stay
              hot forever, and we may need to give it to the next fan if it sits
              much longer.
            </div>
          </div>
        )}

        {paymentPending && !paymentTimedOut && (
          <div
            data-testid="payment-pending-banner"
            className="mb-4 flex items-center gap-3 border-2 border-[#1d4ed8] bg-blue-50 p-3 text-sm text-slate-900"
          >
            <div className="h-3 w-3 animate-pulse bg-[#1d4ed8]" />
            <div>
              <div className="font-heading text-base tracking-wider">
                CONFIRMING PAYMENT…
              </div>
              <div className="text-xs text-slate-600">
                We're finalizing your Stripe payment. This usually takes 1–3
                seconds.
              </div>
            </div>
          </div>
        )}
        {paymentPending && paymentTimedOut && order?.payment_status !== "paid" && (
          <div
            data-testid="payment-timeout-banner"
            className="mb-4 flex items-center gap-3 border-2 border-yellow-500 bg-yellow-50 p-3 text-sm text-slate-900"
          >
            <CircleAlert className="h-5 w-5 flex-shrink-0 text-yellow-600" />
            <div>
              <div className="font-heading text-base tracking-wider">
                STILL CONFIRMING PAYMENT
              </div>
              <div className="text-xs text-slate-600">
                Stripe is taking longer than usual. If you were charged, your
                order will still be prepared — please don't re-order. Show this
                screen to a Boosters volunteer if you're unsure.
              </div>
            </div>
          </div>
        )}
        {order.payment_status === "unpaid" && !paymentPending && (
          <div
            data-testid="payment-unpaid-banner"
            className="mb-4 flex items-center gap-3 border-2 border-yellow-500 bg-yellow-50 p-3 text-sm text-slate-900"
          >
            <CircleAlert className="h-5 w-5 flex-shrink-0 text-yellow-600" />
            <div>
              <div className="font-heading text-base tracking-wider">
                PAYMENT NOT COMPLETED
              </div>
              <div className="text-xs text-slate-600">
                This order was not paid. The kitchen will not prepare it until
                payment is confirmed.
              </div>
            </div>
          </div>
        )}
        <div
          className={`border-2 p-8 text-center transition-colors ${
            isReady
              ? "border-[#16a34a] bg-green-50"
              : "border-slate-900 bg-slate-50"
          }`}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
            Order Number
          </div>
          <div
            data-testid="order-number"
            className="mt-2 font-heading text-7xl tracking-wider text-[#1d4ed8] tabular sm:text-8xl"
          >
            {order.order_number}
          </div>
          {isReady && (
            <div className="mt-4 inline-flex items-center gap-2 bg-[#16a34a] px-4 py-2 text-white">
              <PackageCheck className="h-5 w-5" strokeWidth={2.5} />
              <span className="font-heading text-lg tracking-wider">
                READY · COWBOYS EXPRESS WINDOW
              </span>
            </div>
          )}
        </div>

        {/* Scheduled pickup badge (only when the fan asked for a later time) */}
        {order.scheduled_for && (
          <div
            data-testid="scheduled-pickup-banner"
            className="mt-4 border-2 border-[#7c3aed] bg-purple-50 p-3 flex items-center gap-3"
          >
            <div className="text-2xl">⏰</div>
            <div>
              <div className="font-heading text-sm tracking-[0.2em] text-[#7c3aed] uppercase">
                Scheduled pickup
              </div>
              <div className="text-xs text-slate-700 mt-0.5">
                We'll start cooking around <span className="font-heading">{order.scheduled_for}</span> — head to the Express Window then.
              </div>
            </div>
          </div>
        )}

        {/* Status tracker */}
        <div className="mt-6 border-2 border-slate-900 bg-white">
          <div className="border-b border-slate-200 bg-slate-100 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Status
            </div>
          </div>
          <div className="p-4 space-y-3" data-testid="status-tracker">
            {STEPS.map((s, idx) => {
              const reached = idx <= stepIdx || isCompleted;
              const active = idx === stepIdx && !isCompleted;
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`step-${s.key}`}
                  className={`flex items-center gap-4 border p-3 transition-all ${
                    reached
                      ? "border-[#1d4ed8] bg-blue-50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center ${
                      reached
                        ? "bg-[#1d4ed8] text-white"
                        : "bg-slate-200 text-slate-400"
                    }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-heading text-lg tracking-wider ${
                        reached ? "text-slate-900" : "text-slate-400"
                      }`}
                    >
                      {s.label}
                    </div>
                    {active && (
                      <div className="mt-0.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#1d4ed8]">
                        <span className="h-1.5 w-1.5 animate-pulse bg-[#1d4ed8]" />
                        In progress
                      </div>
                    )}
                  </div>
                  {reached && !active && (
                    <Check className="h-5 w-5 text-[#16a34a]" strokeWidth={3} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Order details */}
        <div className="mt-6 border-2 border-slate-900 bg-white">
          <div className="border-b border-slate-200 bg-slate-100 px-4 py-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Order Details
            </div>
          </div>
          <div className="p-4 space-y-2">
            {order.items.map((it, idx) => (
              <div
                key={`${it.item_id}-${idx}`}
                className="flex items-start justify-between text-sm"
              >
                <div className="flex-1 min-w-0 pr-2">
                  <span className="font-heading text-base text-[#1d4ed8] tabular">
                    {it.quantity}×
                  </span>{" "}
                  {it.name}
                  {(it.variant_choice || it.side_choice || it.drink_choice) && (
                    <span className="ml-1 text-xs uppercase tracking-[0.15em] text-[#1d4ed8]">
                      · {[it.variant_choice, it.side_choice, it.drink_choice].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {Array.isArray(it.addons) && it.addons.length > 0 && (
                    <div className="mt-0.5 text-xs uppercase tracking-[0.15em] text-emerald-700">
                      {it.addons
                        .map((a) => `+ ${a.label}${a.qty > 1 ? ` ×${a.qty}` : ""}`)
                        .join(" · ")}
                    </div>
                  )}
                </div>
                <span className="text-slate-500 tabular whitespace-nowrap">
                  ${(it.price * it.quantity).toFixed(2)}
                </span>
              </div>
            ))}
            <div className="border-t border-slate-300 pt-3 mt-3 flex items-baseline justify-between">
              <span className="font-heading text-xl tracking-wider">TOTAL</span>
              <span className="font-heading text-2xl text-[#1d4ed8] tabular">
                ${order.total.toFixed(2)}
              </span>
            </div>
            {order.tip > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Includes tip for Boosters</span>
                <span className="font-heading text-sm text-[#1d4ed8] tabular">
                  ${order.tip.toFixed(2)}
                </span>
              </div>
            )}
            <div className="text-xs text-slate-500 pt-2">
              Pickup for:{" "}
              <span className="text-slate-900 font-medium">
                {order.customer_name}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.25em] text-slate-400">
          <Clock className="h-3 w-3" />
          Status updates every few seconds
        </div>

        <FeedbackBox />
      </main>
    </div>
  );
}

function FeedbackBox() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("compliment");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        kind,
        message: message.trim(),
        name: name.trim() || null,
      });
      setSent(true);
      setMessage("");
      setName("");
    } catch (err) {
      // Silent-fail here would leave the fan thinking their feedback was
      // submitted when it wasn't (post-game complaint lost forever). Show a
      // toast so they can retry or copy their message.
      toast.error(
        err?.response?.data?.detail
          || "Couldn't send feedback — please try again or grab a volunteer",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-6 text-center">
        <button
          data-testid="open-feedback-btn"
          onClick={() => setOpen(true)}
          className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8] hover:text-[#1e40af] underline underline-offset-4"
        >
          Send Feedback · Suggestion · Complaint · Compliment
        </button>
      </div>
    );
  }
  if (sent) {
    return (
      <div className="mt-6 border-2 border-emerald-600 bg-emerald-50 p-4 text-center" data-testid="feedback-sent">
        <div className="font-heading text-lg tracking-wider text-emerald-700">THANK YOU!</div>
        <div className="mt-1 text-sm text-slate-700">
          Your message reached the Boosters. Go Cowboys!
        </div>
        <button
          onClick={() => { setSent(false); setOpen(false); }}
          className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8] underline underline-offset-4"
        >
          Close
        </button>
      </div>
    );
  }
  const kinds = [
    { k: "compliment", label: "Compliment", color: "#16a34a" },
    { k: "suggestion", label: "Suggestion", color: "#7c3aed" },
    { k: "complaint", label: "Complaint", color: "#dc2626" },
  ];
  return (
    <div className="mt-6 border-2 border-slate-900 bg-white p-4 space-y-3" data-testid="feedback-form">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">Send Feedback</div>
          <div className="text-xs text-slate-500">Suggestion · Complaint · Compliment</div>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-slate-600"
        >
          Close
        </button>
      </div>
      <div className="flex gap-2">
        {kinds.map((k) => (
          <button
            key={k.k}
            data-testid={`feedback-kind-${k.k}`}
            onClick={() => setKind(k.k)}
            className={`flex-1 border-2 py-2 font-heading text-[11px] tracking-wider ${
              kind === k.k ? "text-white" : "text-slate-700 bg-white"
            }`}
            style={
              kind === k.k
                ? { borderColor: k.color, background: k.color }
                : { borderColor: "#cbd5e1" }
            }
          >
            {k.label}
          </button>
        ))}
      </div>
      <input
        data-testid="feedback-name"
        type="text"
        placeholder="Your name (optional)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full border border-slate-300 px-3 py-2 text-sm"
      />
      <textarea
        data-testid="feedback-message"
        placeholder="Tell us what's on your mind…"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={2000}
        className="w-full border border-slate-300 px-3 py-2 text-sm"
      />
      <button
        data-testid="feedback-submit"
        onClick={submit}
        disabled={busy || !message.trim()}
        className="w-full border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-50"
      >
        {busy ? "SENDING…" : "SEND MESSAGE"}
      </button>
    </div>
  );
}
